import React, { useEffect, useState } from 'react';

import { CharacterSheetInterface, Skill, SKILLS } from "@dndModels";
import { AbilityScoreType } from '@dndModels/Types';

import FlexBox from '@components/layout/FlexBox';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import { ABILITY_SCORES, ONES_0_to_50, PROFICIENCY_OPTIONS } from '@dndModels/Constants';
import DandDCharacterUtilities from '@dndUtilities/char_utilities';

interface Props {
  char: CharacterSheetInterface;
}

const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterSkillCustomizations({
  char,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [editing_skill, setEditingSkill] = useState<Skill | undefined>(undefined);
  const [ping, setPing] = useState(false);

  const customizations = char.customizations;

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const renderSkillCustomization = (skill: Skill) => {
    // debugger;
    const { char_roll } = char_util.skillRolls(char, skill);
    return (
      <>
        <FlexBox direction="column" className="clickable" onClick={() => setEditingSkill(editing_skill && editing_skill._id === skill._id ? undefined : skill) }>
          <FlexBox className="header">
            { skill.name }: Current { char_roll?.modifier??0 }
          </FlexBox>
          {char_roll && 
            <FlexBox className="small_label gray">
              { char_roll.proficiency === 1 ? 
                "Proficient" 
              : char_roll.proficiency === 2 ? 
                "Expert" 
              : char_roll.proficiency === 0.5 ? 
                "Half Proficient" : ""
              }
            </FlexBox>
          }
          { customizations.prof_overrides[skill._id] && Object.keys(customizations.prof_overrides[skill._id]).length > 0 &&
            <FlexBox className="small_label gray">(Overridden)</FlexBox>
          }
        </FlexBox>
      </>
    );
  }

  const renderSelectedField = () => {
    if (editing_skill) {
      return (
        <>
          <FlexBox xs_expand={0} className="header centered">Customizations for { editing_skill.name }</FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Proficiency Level"
              value={ customizations.prof_overrides[editing_skill._id] && customizations.prof_overrides[editing_skill._id].prof_level !== undefined ? `${customizations.prof_overrides[editing_skill._id].prof_level}` : "Don't Override" }
              options={PROFICIENCY_OPTIONS}
              extra_options={["Don't Override"]}
              onChange={(changed: string) => {
                if (changed === "Don't Override") {
                  delete customizations.prof_overrides[editing_skill._id].prof_level;
                } else {
                  if (!customizations.prof_overrides[editing_skill._id]) {
                    customizations.prof_overrides[editing_skill._id] = {};
                  }
                  customizations.prof_overrides[editing_skill._id].prof_level = +changed;
                }
                setPing(!ping);
                char.changed();
                char.subscription_holder.recalculate();
              }}
            />
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Use Ability Score"
              value={ customizations.prof_overrides[editing_skill._id] && customizations.prof_overrides[editing_skill._id].stat_override !== undefined ? customizations.prof_overrides[editing_skill._id].stat_override : "Don't Override" }
              options={ABILITY_SCORES}
              extra_options={["Don't Override"]}
              onChange={(changed: AbilityScoreType | "Don't Override") => {
                if (changed === "Don't Override") {
                  delete customizations.prof_overrides[editing_skill._id].stat_override;
                } else {
                  if (!customizations.prof_overrides[editing_skill._id]) {
                    customizations.prof_overrides[editing_skill._id] = {};
                  }
                  customizations.prof_overrides[editing_skill._id].stat_override = changed;
                }
                setPing(!ping);
                char.changed();
                char.subscription_holder.recalculate();
              }}
            />
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              value={ customizations.prof_overrides[editing_skill._id] && customizations.prof_overrides[editing_skill._id].override !== undefined ? `${customizations.prof_overrides[editing_skill._id].override}` : "Don't Override" }
              options={["-5","-4","-3","-2","-1",...ONES_0_to_50]}
              extra_options={["Don't Override"]}
              onChange={(changed: string) => {
                if (changed === "Don't Override") {
                  delete customizations.prof_overrides[editing_skill._id].override;
                } else {
                  if (!customizations.prof_overrides[editing_skill._id]) {
                    customizations.prof_overrides[editing_skill._id] = {};
                  }
                  customizations.prof_overrides[editing_skill._id].override = +changed;
                }
                setPing(!ping);
                char.changed();
                char.subscription_holder.recalculate();
              }}
            />
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Bonus"
              value={ customizations.prof_overrides[editing_skill._id] && customizations.prof_overrides[editing_skill._id].bonus !== undefined ? `${customizations.prof_overrides[editing_skill._id].bonus}` : "Don't Override" }
              options={["-5","-4","-3","-2","-1",...ONES_0_to_50]}
              extra_options={["Don't Override"]}
              onChange={(changed: string) => {
                if (changed === "Don't Override") {
                  delete customizations.prof_overrides[editing_skill._id].bonus;
                } else {
                  if (!customizations.prof_overrides[editing_skill._id]) {
                    customizations.prof_overrides[editing_skill._id] = {};
                  }
                  customizations.prof_overrides[editing_skill._id].bonus = +changed;
                }
                setPing(!ping);
                char.changed();
                char.subscription_holder.recalculate();
              }}
            />
          </FlexBox>
        </>
      );
    }
    return null;
  }

  return (
    <FlexBox direction="row">
      <FlexBox xs_expand={0}>
        <FlexBox direction="column" style={{ width: "250px" }}>
          { SKILLS.map(skill => {
            return (
              <FlexBox key={skill._id}>
                { renderSkillCustomization(skill) }
              </FlexBox>
            );
          })}
        </FlexBox>
      </FlexBox>
      <FlexBox xs_expand={1} direction="column">
        { renderSelectedField() }
      </FlexBox>
    </FlexBox>
  );
}

